import {
  inject,
  onUnmounted,
  ref,
  watch,
  type ComputedRef,
  type InjectionKey,
  type Ref,
} from 'vue';

export interface PageTabNavItem {
  key: string;
  title: string;
  disabled?: boolean;
}

export interface PageTabNavApi {
  tabs: Ref<PageTabNavItem[]>;
  activeKey: Ref<string>;
  register: (
    owner: string,
    tabs: PageTabNavItem[],
    activeKey: string,
    onSelect: (key: string) => void | Promise<void>,
  ) => void;
  clear: (owner: string) => void;
  select: (key: string) => Promise<void>;
}

export const PAGE_TAB_NAV_KEY: InjectionKey<PageTabNavApi> = Symbol('pageTabNav');

export function createPageTabNav(): PageTabNavApi {
  const tabs = ref<PageTabNavItem[]>([]);
  const activeKey = ref('');
  let owner = '';
  let onSelect: ((key: string) => void | Promise<void>) | null = null;

  const register = (
    nextOwner: string,
    nextTabs: PageTabNavItem[],
    nextActiveKey: string,
    nextOnSelect: (key: string) => void | Promise<void>,
  ) => {
    owner = nextOwner;
    tabs.value = nextTabs;
    activeKey.value = nextActiveKey;
    onSelect = nextOnSelect;
  };

  const clear = (nextOwner: string) => {
    if (owner !== nextOwner) return;
    owner = '';
    tabs.value = [];
    activeKey.value = '';
    onSelect = null;
  };

  const select = async (key: string) => {
    const tab = tabs.value.find((item) => item.key === key);
    if (!tab || tab.disabled) return;
    await onSelect?.(key);
  };

  return { tabs, activeKey, register, clear, select };
}

export function usePageTabNav(
  owner: string,
  tabs: ComputedRef<PageTabNavItem[]> | Ref<PageTabNavItem[]>,
  activeKey: { readonly value: string },
  onSelect: (key: string) => void | Promise<void>,
) {
  const api = inject(PAGE_TAB_NAV_KEY, null);
  if (!api) return;

  watch(
    [tabs, activeKey],
    () => {
      api.register(owner, tabs.value, activeKey.value, onSelect);
    },
    { immediate: true, deep: true },
  );

  onUnmounted(() => api.clear(owner));
}
